import { Button, CircularProgress } from "@mui/material";
import useEmployeeDataStore from "../../Zustand/Store/useEmployeeDataStore";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const str = String(dateStr);
  if (str.includes("T")) return str.split("T")[0];
  return str;
};

const buildProfileImageUrl = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (
    raw.startsWith("employees/") ||
    raw.startsWith("persons/") ||
    raw.startsWith("interns/")
  ) {
    return `${MAIN_URL}/storage/app/public/${raw}`;
  }
  return `${MAIN_URL}/storage/app/public/employees/${raw}`;
};

const toSelectId = (value) =>
  value === null || value === undefined || value === "" ? "" : Number(value);

const getFirstDepartmentLocation = (employeeData) => {
  const raw =
    employeeData?.department_location ??
    employeeData?.departmentLocation ??
    null;

  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  if (typeof raw === "object") return raw;
  return null;
};

const resolveDepartmentFields = (employeeData) => {
  const deptLoc = getFirstDepartmentLocation(employeeData);
  const nestedDept = deptLoc?.department || deptLoc?.Department;

  const organization_department_location_id = toSelectId(
    employeeData?.organization_department_location_id ??
      deptLoc?.organization_department_location_id
  );

  const organization_department_id = toSelectId(
    employeeData?.organization_department_id ??
      deptLoc?.organization_department_id ??
      nestedDept?.organization_department_id
  );

  const location = deptLoc?.location || deptLoc?.Location;

  return {
    organization_department_id,
    organization_department_location_id,
    department_name:
      employeeData?.department_name ||
      nestedDept?.department_name ||
      "",
    location_name:
      employeeData?.location_name ||
      location?.location_name ||
      "",
  };
};

const mergePersonDataIntoEmployee = (employeeData) => {
  if (!employeeData) return employeeData;

  const orgPersons = employeeData.org_persons || employeeData.OrgPersons;
  const person = orgPersons?.person || orgPersons?.Person;
  const deptFields = resolveDepartmentFields(employeeData);

  const merged = {
    ...employeeData,
    organization_person_id: toSelectId(
      employeeData.organization_person_id || orgPersons?.organization_person_id
    ),
    employee_id: toSelectId(employeeData.employee_id),
    employee_code:
      employeeData.employee_code || orgPersons?.person_code || "",
    first_name: employeeData.first_name || person?.first_name || "",
    middle_name:
      employeeData.middle_name ?? person?.middle_name ?? "",
    last_name: employeeData.last_name || person?.last_name || "",
    date_of_birth: employeeData.date_of_birth || person?.date_of_birth || "",
    gender: employeeData.gender || person?.gender || "",
    marital_status:
      employeeData.marital_status || person?.marital_status || "",
    profile_image_url: buildProfileImageUrl(
      person?.profile_image_url || employeeData.profile_image_url
    ),
    date_of_joining:
      employeeData.date_of_joining || orgPersons?.organization_join_date || "",
    organization_designation_id: toSelectId(
      employeeData.organization_designation_id
    ),
    organization_work_shift_id: toSelectId(
      employeeData.organization_work_shift_id
    ),
    work_shift_name:
      employeeData.work_shift_name ||
      employeeData.workmodel?.work_shift_name ||
      employeeData.workmodel?.work_model_name ||
      "",
    designation_name: employeeData.designation_name || "",
    ...deptFields,
  };

  return merged;
};

const SubmitButton = ({ mode }) => {
  const {
    Employee,
    setEmployeeErrors,
    setEmployee,
    Education,
    Address,
    Contact,
    setEducation,
    setAddress,
    setContact,
    resetData,
  } = useEmployeeDataStore();

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(mode === "edit" || mode === "view");

  useEffect(() => {
    const fetchEmployeeData = async () => {
      if ((mode !== "edit" && mode !== "view") || !id || !org?.organization_id) {
        setFetching(false);
        return;
      }

      setFetching(true);
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-store/update/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        let mergedEmployee = mergePersonDataIntoEmployee(
          response.data.employee
        );
        mergedEmployee.date_of_birth = formatDate(mergedEmployee.date_of_birth);
        mergedEmployee.date_of_joining = formatDate(
          mergedEmployee.date_of_joining
        );

        // Match HR ERP: department id lives on department_location relation
        const deptLoc = getFirstDepartmentLocation(mergedEmployee);
        if (deptLoc) {
          mergedEmployee.organization_department_id = toSelectId(
            deptLoc.organization_department_id ??
              deptLoc.department?.organization_department_id
          );
          if (!mergedEmployee.organization_department_location_id) {
            mergedEmployee.organization_department_location_id = toSelectId(
              deptLoc.organization_department_location_id
            );
          }
        }

        // Fallback: resolve department from location id when relation array is empty
        if (
          !mergedEmployee.organization_department_id &&
          mergedEmployee.organization_department_location_id
        ) {
          try {
            const locRes = await axios.get(
              `${MAIN_URL}/api/organizations/${org.organization_id}/department-location/${mergedEmployee.organization_department_location_id}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            const loc =
              locRes.data?.departmentlocation ||
              locRes.data?.data ||
              locRes.data;
            mergedEmployee.organization_department_id = toSelectId(
              loc?.organization_department_id ??
                loc?.department?.organization_department_id
            );
          } catch (locErr) {
            console.warn("Could not resolve department from location:", locErr);
          }
        }

        setEmployee(mergedEmployee);

        setEducation(
          response.data.education?.length > 0
            ? response.data.education
            : [
                {
                  organization_education_level_id: "",
                  organization_education_degree_id: "",
                  organization_education_stream_id: "",
                  institute_name: "",
                  board_university_name: "",
                  marks_percentage: "",
                  year_of_passing: "",
                  is_pursuing: "",
                  employee_id: mergedEmployee.employee_id || "",
                  organization_id: org.organization_id,
                },
              ]
        );

        const addressRows =
          response.data.address?.length > 0
            ? response.data.address.map((item) => ({
                ...item,
                city: item.city || null,
              }))
            : [
                {
                  organization_employee_address_type_id: "",
                  address_line1: "",
                  address_line2: "",
                  address_line3: "",
                  general_city_id: "",
                  postal_code: "",
                  organization_employee_residential_ownership_type_id: "",
                  employee_id: mergedEmployee.employee_id || "",
                  organization_id: org.organization_id,
                },
              ];
        setAddress(addressRows);

        setContact(
          response.data.contact?.length > 0
            ? response.data.contact
            : [
                {
                  employee_id: mergedEmployee.employee_id || "",
                  personal_phone_number: "",
                  alternate_personal_phone_number: "",
                  personal_email: "",
                  alternate_personal_email: "",
                  preferred_contact_method: "",
                  emergency_person_phone_number_1: "",
                  emergency_person_name_1: "",
                  emergency_person_relation_1: "",
                  emergency_person_phone_number_2: "",
                  emergency_person_name_2: "",
                  emergency_person_relation_2: "",
                  work_phone_number: "",
                  work_email: "",
                  organization_id: org.organization_id,
                },
              ]
        );
      } catch (error) {
        console.error("Error fetching employee data:", error);
        toast.error("Failed to load employee data");
      } finally {
        setFetching(false);
      }
    };

    fetchEmployeeData();
  }, [mode, id, org?.organization_id, setEmployee, setEducation, setAddress, setContact]);

  const validateForm1 = () => {
    const errors = {};

    if (!Employee.employee_code)
      errors.employee_code = "Employee Code is required.";
    if (!Employee.first_name) errors.first_name = "First Name is required.";
    if (!Employee?.date_of_birth)
      errors.date_of_birth = "Date of Birth is required.";
    if (!Employee?.gender) errors.gender = "Gender is required.";
    if (!Employee?.marital_status)
      errors.marital_status = "Marital Status is required.";
    if (!Employee.organization_department_id)
      errors.organization_department_id = "Department is required.";
    if (!Employee.organization_designation_id)
      errors.organization_designation_id = "Designation is required.";
    if (!Employee.organization_department_location_id)
      errors.organization_department_location_id = "Location is required.";
    if (!Employee.organization_work_shift_id)
      errors.organization_work_shift_id = "Workshift is required.";
    if (!Employee.date_of_joining)
      errors.date_of_joining = "Date of Joining is required.";

    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      toast.error(errors[firstKey]);
      setEmployeeErrors(errors);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (mode === "view") return;
    if (!validateForm1()) return;

    setLoading(true);
    try {
      const formData = new FormData();

      Object.entries(Employee).forEach(([key, value]) => {
        if (key === "profile_image_url" && value instanceof File) {
          formData.append("profile_image_url", value);
        } else if (typeof value === "object" && value !== null) {
          // skip nested objects (org_persons, department_location, etc.)
        } else {
          formData.append(key, value ?? "");
        }
      });

      if (mode === "edit" && id) {
        formData.append("organization_person_id", id);
      }

      const response = await axios.post(
        `${MAIN_URL}/api/organizations/${org.organization_id}/employee-store/store1`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const empPersonId =
        response.data?.employee?.organization_person_id ||
        Employee?.organization_person_id ||
        id;
      const empId =
        response.data?.employee?.employee_id || Employee?.employee_id;

      const sectionData = {};

      const filledEducation = Education?.filter(
        (item) =>
          item.organization_education_level_id ||
          item.organization_education_degree_id ||
          item.institute_name ||
          item.board_university_name ||
          item.marks_percentage ||
          item.year_of_passing
      );

      const filledAddress = Address?.filter(
        (item) =>
          item.organization_employee_address_type_id ||
          item.address_line1 ||
          item.postal_code ||
          item.general_city_id
      );

      const filledContact = Contact?.filter(
        (item) =>
          item.personal_phone_number ||
          item.personal_email ||
          item.alternate_personal_phone_number
      );

      if (filledEducation.length > 0) {
        sectionData.education = Education.map((item) => ({
          ...item,
          employee_id: empId,
          organization_person_id: empPersonId,
          organization_id: org.organization_id,
        }));
      }
      if (filledAddress.length > 0) {
        sectionData.address = Address.map((item) => ({
          ...item,
          employee_id: empId,
          organization_person_id: empPersonId,
          organization_id: org.organization_id,
          city: undefined,
        }));
      }
      if (filledContact.length > 0) {
        sectionData.contact = Contact.map((item) => ({
          ...item,
          employee_id: empId,
          organization_person_id: empPersonId,
          organization_id: org.organization_id,
        }));
      }

      if (Object.keys(sectionData).length > 0) {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-store/store2`,
          sectionData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      toast.success(
        mode === "edit"
          ? "Employee updated successfully!"
          : "Employee added successfully!"
      );
      resetData();
      navigate(-1);
    } catch (error) {
      if (error.response?.data?.errors) {
        if (error.response.data.section === "employee") {
          setEmployeeErrors(error.response.data.errors);
          toast.error("Please fix validation errors in employee details.");
        } else {
          toast.error("Validation failed. Please check all sections.");
        }
      } else {
        console.error("Submit Error:", error);
        toast.error("Failed to save employee");
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Button
        variant="contained"
        disabled
        sx={{ borderRadius: 2, marginTop: 6, minWidth: 120 }}
      >
        <CircularProgress size={22} color="inherit" />
      </Button>
    );
  }

  return (
    <Button
      variant="contained"
      color="primary"
      size="medium"
      sx={{
        borderRadius: 2,
        marginTop: 6,
        minWidth: 120,
        textTransform: "capitalize",
        fontWeight: 500,
      }}
      onClick={handleSubmit}
      disabled={loading || mode === "view"}
    >
      {loading ? (
        <CircularProgress size={22} color="inherit" />
      ) : mode === "edit" ? (
        "Update"
      ) : (
        "Submit"
      )}
    </Button>
  );
};

export default SubmitButton;
